import { FC, RefObject, useMemo, useRef } from "react";

import {
  EvalError,
  EvalPlan,
  EvalResults,
  EvalSpec,
  EvalStats,
} from "@tsmono/inspect-common/types";

import { EvalLogStatus } from "../../../@types/extraInspect";
import { SampleSummary } from "../../../client/api/types";
import { MessageBand } from "../../../components/MessageBand";
import { kLogViewInfoTabId } from "../../../constants";
import { useTotalSampleCount } from "../../../state/hooks";
import { PlanCard } from "../../plan/PlanCard";

// Individual hook for Info tab
export const useInfoTabConfig = (
  evalSpec: EvalSpec | undefined,
  evalPlan: EvalPlan | undefined,
  evalError: EvalError | undefined | null,
  evalResults: EvalResults | undefined | null,
  evalStatus: EvalLogStatus | undefined | null,
  metadata?: Record<string, unknown>
) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const totalSampleCount = useTotalSampleCount();
  return useMemo(() => {
    return {
      id: kLogViewInfoTabId,
      label: "Info",
      scrollable: true,
      component: InfoTab,
      componentProps: {
        evalSpec,
        evalPlan,
        evalError,
        evalResults,
        evalStatus,
        metadata,
        sampleCount: totalSampleCount,
        scrollRef,
      },
      scrollRef,
    };
  }, [
    evalSpec,
    evalPlan,
    evalError,
    evalResults,
    evalStatus,
    metadata,
    totalSampleCount,
  ]);
};

interface InfoTabProps {
  evalSpec?: EvalSpec;
  evalPlan?: EvalPlan;
  evalStats?: EvalStats;
  evalResults?: EvalResults;
  samples?: SampleSummary[];
  evalStatus?: EvalLogStatus;
  metadata?: Record<string, unknown>;
  sampleCount?: number;
  scrollRef: RefObject<HTMLDivElement | null>;
}

export const InfoTab: FC<InfoTabProps> = ({
  evalSpec,
  evalPlan,
  evalResults,
  evalStatus,
  metadata,
  sampleCount,
  scrollRef,
}) => {
  const showWarning =
    sampleCount === 0 &&
    evalStatus === "success" &&
    evalSpec?.dataset.samples &&
    evalSpec.dataset.samples > 0;

  return (
    <div style={{ width: "100%" }}>
      {showWarning ? (
        <MessageBand
          id="sample-too-large"
          message="Unable to display samples (this evaluation log may be too large)."
          type="warning"
        />
      ) : (
        ""
      )}
      <div style={{ padding: "0.5em 1em 0 1em", width: "100%" }}>
        <PlanCard
          evalSpec={evalSpec}
          evalPlan={evalPlan}
          scores={evalResults?.scores}
          metadata={metadata}
          scrollRef={scrollRef}
        />
      </div>
    </div>
  );
};
